// =============================================================================
// Shared invoice utilities — single source of truth for status logic
// =============================================================================

export const INVOICE_STATUSES = ['draft', 'sent', 'paid', 'partially_paid', 'overdue', 'void'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'void', label: 'Void' },
] as const;

/**
 * Valid status transitions enforced by backend.
 * Key = current status, Value = array of allowed next statuses.
 */
export const VALID_TRANSITIONS: Record<string, string[]> = {
  draft: ['sent', 'void'],
  sent: ['paid', 'partially_paid', 'overdue', 'void'],
  partially_paid: ['paid', 'void'],
  overdue: ['paid', 'partially_paid', 'void'],
  paid: [],    // terminal — use credit notes for refunds
  void: [],    // terminal
};

export function getInvoiceStatusBadgeVariant(
  status: string,
): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'paid':
      return 'success';
    case 'sent':
      return 'default';
    case 'overdue':
      return 'destructive';
    case 'partially_paid':
      return 'warning';
    case 'void':
      return 'warning';
    case 'draft':
    default:
      return 'secondary';
  }
}

export function getInvoiceStatusLabel(status: string): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'sent':
      return 'Sent';
    case 'paid':
      return 'Paid';
    case 'partially_paid':
      return 'Partially Paid';
    case 'overdue':
      return 'Overdue';
    case 'void':
      return 'Void';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

/**
 * Returns the allowed next statuses for a given current status.
 */
export function getAllowedTransitions(currentStatus: string): string[] {
  return VALID_TRANSITIONS[currentStatus] ?? [];
}
